// "Take-off spreadsheet" (admins, #516): this option written into Templafy's
// current STM002 Repaint Take Off, for when the job has to carry on in the
// spreadsheet, or for a reviewer who can't open the app. Built on the server
// and saved to the job's Proposal & Take-Off folder — several workbooks when
// the job is bigger than one take-off holds. Shown to HigginsApps-Dev only;
// the API checks it again.
//
// Like every generated file, it lives only on SharePoint: the first workbook
// opens in the tab opened on the tap; the browser blocks tabs opened later, so
// any others are links (lib/file-handoff.ts).
import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button, OutboundLink } from '@higgins/ui';
import { ApiError, exportTakeoffApi, isConnectionFailure, type TakeoffExportFromApi } from '../lib/api';
import { isoToday } from '../lib/dates';
import { openedWhere, openWaitingTab } from '../lib/file-handoff';
import { meAtom } from '../store/atoms';
import { ErrorMessage } from './message';

const meLoadable = loadable(meAtom);

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'Making the take-off spreadsheet needs signal.';
  if (err instanceof ApiError) return err.message;
  return 'The take-off spreadsheet could not be made.';
}

interface Props {
  optionId: string;
  /** Why it can't be made right now (changes still syncing: it's made from what CRM has); null = it can. The REVIEW is done in the workbook. */
  blockedBecause: string | null;
}

export function TakeoffExportPanel({ optionId, blockedBecause }: Props) {
  const me = useAtomValue(meLoadable);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<TakeoffExportFromApi | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (me.state !== 'hasData' || !me.data.admin) return null;

  async function make() {
    setBusy(true);
    setSaved(null);
    setError(null);
    const tab = openWaitingTab('Making the take-off spreadsheet…');
    try {
      const result = await exportTakeoffApi(optionId, isoToday());
      if (tab && result.files[0]) tab.show(result.files[0].webUrl);
      else tab?.close();
      setSaved(result);
    } catch (err) {
      tab?.close();
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  const several = (saved?.files.length ?? 0) > 1;
  return (
    <div className="mt-3 border-t border-slate-200 pt-3">
      <h4 className="mb-1 font-medium text-slate-900">Take-off spreadsheet (admin)</h4>
      <p className="mb-2 text-xs text-slate-500">
        This option in the current STM002 take-off from Templafy, for carrying on in the spreadsheet or for a reviewer outside the app. The workbook works out the price itself when it opens. A job too big for one take-off comes as several.
      </p>
      <Button size="sm" variant="outline" onClick={() => void make()} disabled={busy || !!blockedBecause}>
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />} {busy ? 'Making the spreadsheet…' : 'Take-off spreadsheet'}
      </Button>
      {blockedBecause && <p className="mt-1 text-xs text-slate-500">{blockedBecause}</p>}
      {error && <ErrorMessage className="mt-2">{error}</ErrorMessage>}
      {saved && (
        <div className="mt-2 text-sm text-green-800">
          <p>
            {several ? `${saved.files.length} workbooks` : 'The workbook'} saved to the job&rsquo;s Proposal &amp; Take-Off folder, from {saved.template}
            {several ? `. The first ${openedWhere()}; open the others here:` : `, and ${openedWhere()}.`}
          </p>
          <ul className="mt-1 list-disc pl-5">
            {saved.files.map((f) => (
              <li key={f.webUrl}>
                <OutboundLink openIn="edge" href={f.webUrl} className="text-primary underline">
                  {f.fileName}
                </OutboundLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
